import { create } from "zustand";
import { Brief } from "../../types/types";
import { toast } from "sonner";
import { useCampaignStore } from "./campaignStore";

/**
 * Activation messages live in two places client-side: the brief store and
 * `campaign.partnership.tiers[].activation_message` in the campaign store
 * (which the approve-creator dialog reads). The backend mirrors the brief
 * field into the tier on save, so mirror it locally too — otherwise the
 * dialog shows a stale message until the campaign is refetched.
 */
const syncTierActivationMessage = (field: string, newValue: string) => {
  const tierMatch = field.match(/^activation_message_tier_(\d+)$/);
  if (!tierMatch) return;
  const order = parseInt(tierMatch[1], 10);
  const { campaign, updateCampaign } = useCampaignStore.getState();
  if (!campaign?.partnership?.tiers) return;
  updateCampaign({
    partnership: {
      ...campaign.partnership,
      tiers: campaign.partnership.tiers.map((tier) =>
        tier.order === order
          ? { ...tier, activation_message: newValue }
          : tier,
      ),
    },
  });
};

interface BriefState {
  brief: Brief | null;
  isLoading: boolean;
  error: string | null;
  includeCreatorPortal: boolean;

  setBrief: (brief: Brief) => void;
  setIncludeCreatorPortal: (include: boolean) => void;

  saveBrief: (
    campaignId: string | null,
    newValue: string,
    field: string,
    token: string
  ) => Promise<boolean>;

  updateBriefMode: (
    campaignId: string | null,
    mode: "guided" | "freeform",
    token: string
  ) => Promise<boolean>;

  copyBriefFrom: (
    sourceCampaignId: string,
    targetCampaignId: string,
    token: string
  ) => Promise<boolean>;

  updateIncludeCreatorPortal: (
    campaign_id: string,
    include: boolean,
    token: string
  ) => Promise<boolean>;

  generateBriefSection: (
    prompt: string,
    currentContent: any,
    campaignId: string,
    token: string
  ) => Promise<string | null>;

  reset: () => void;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export const useBriefStore = create<BriefState>((set) => ({
  brief: null,
  isLoading: false,
  error: null,
  includeCreatorPortal: false,

  setBrief: (brief: Brief) => set({ brief }),

  setIncludeCreatorPortal: (include: boolean) =>
    set({ includeCreatorPortal: include }),

  saveBrief: async (
    campaignId: string | null,
    newValue: string,
    field: string,
    token: string
  ) => {
    set({ isLoading: true, error: null });
    try {
      if (!campaignId) throw new Error("Campaign ID is null");

      const response = await fetch(
        `${API_BASE_URL}/api/campaigns/updateBrief`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            campaign_id: campaignId,
            newValue: newValue.trim(),
            field,
          }),
        }
      );

      if (!response.ok) {
        set({ isLoading: false });
        return false;
      }

      // Update local state
      const data = await response.json();
      if (data.brief) {
        set({ brief: data.brief, isLoading: false });
      } else {
        set({ isLoading: false });
      }

      syncTierActivationMessage(field, newValue.trim());

      return true;
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
      return false;
    }
  },

  generateBriefSection: async (
    prompt: string,
    currentContent: any,
    campaignId: any,
    token: string
  ) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/campaigns/generateBriefSection`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            prompt,
            current_content: currentContent,
            campaign_id: campaignId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to generate content");
      }

      const data = await response.json();
      return data.content;
    } catch (error) {
      console.error("AI generation error:", error);
      return null;
    }
  },

  updateBriefMode: async (
    campaignId: string | null,
    mode: "guided" | "freeform",
    token: string
  ) => {
    set({ isLoading: true, error: null });
    try {
      if (!campaignId) throw new Error("Campaign ID is null");

      const response = await fetch(
        `${API_BASE_URL}/api/campaigns/updateBriefMode`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            campaign_id: campaignId,
            mode,
          }),
        }
      );

      if (!response.ok) {
        set({ isLoading: false });
        return false;
      }

      // Update local state
      const data = await response.json();
      if (data.brief) {
        set({ brief: data.brief, isLoading: false });
      } else {
        set({ isLoading: false });
      }

      return true;
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
      return false;
    }
  },

  copyBriefFrom: async (
    sourceCampaignId: string,
    targetCampaignId: string,
    token: string
  ) => {
    set({ isLoading: true, error: null });
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/campaigns/copyBrief`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            source_campaign_id: sourceCampaignId,
            target_campaign_id: targetCampaignId,
          }),
        }
      );

      if (!response.ok) {
        set({ isLoading: false });
        return false;
      }

      const data = await response.json();
      if (data.brief) {
        set({ brief: data.brief, isLoading: false });
      } else {
        set({ isLoading: false });
      }

      return true;
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
      return false;
    }
  },

  updateIncludeCreatorPortal: async (
    campaign_id: string,
    include: boolean,
    token: string
  ) => {
    set({ isLoading: true, error: null });
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/campaigns/updateIncludeCreatorPortal`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            campaign_id: campaign_id,
            value: include,
          }),
        }
      );

      if (!response.ok) {
        set({ isLoading: false });
        return false;
      } else {
        const data = await response.json();
        toast.success(data.message || "Include Creator Portal updated successfully");
        set({ includeCreatorPortal: include, isLoading: false });
        return true;
      }
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
      return false;
    }
  },

  reset: () =>
    set({
      brief: null,
      isLoading: false,
      error: null,
    }),
}));
